import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data/siteContent";

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const lenis = useLenis();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        lenis?.scrollTo(href, { offset: -64, duration: 1.4 });
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-500 ${
                scrolled
                    ? "bg-[#FAFAFA]/85 backdrop-blur-md border-[#D9D9D9]"
                    : "bg-transparent border-transparent"
            }`}
        >
            <div className="flex items-center justify-between px-6 md:px-12 h-16 md:h-20">
                <button
                    data-testid="nav-logo-button"
                    onClick={() => go("#top")}
                    className="flex items-center group"
                >
                    <img
                        src="/logo-black.png"
                        alt="First Choice Trading Company"
                        className="h-9 md:h-11 w-auto transition-opacity duration-300 group-hover:opacity-75"
                    />
                </button>

                <nav className="hidden md:flex items-center gap-8" data-testid="nav-desktop-links">
                    {NAV_LINKS.map((link) => (
                        <button
                            key={link.href}
                            data-testid={`nav-link-${link.label.toLowerCase()}`}
                            onClick={() => go(link.href)}
                            className="group flex items-baseline gap-2 text-sm uppercase tracking-[0.18em] text-[#595959] hover:text-[#111111] transition-colors duration-300"
                        >
                            <span className="text-[10px] text-copper font-medium">{link.index}</span>
                            {link.label}
                        </button>
                    ))}
                    <button
                        data-testid="nav-quote-button"
                        onClick={() => go("#contact")}
                        className="bg-copper text-[#FAFAFA] px-5 py-2.5 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-copper-dark transition-colors duration-300"
                    >
                        Get a Quote
                    </button>
                </nav>

                <button
                    data-testid="nav-mobile-menu-button"
                    className="md:hidden text-[#111111] p-2"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="nav-mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: "easeInOut" }}
                        className="md:hidden overflow-hidden bg-[#FAFAFA]/95 backdrop-blur-md border-t border-[#D9D9D9]"
                    >
                        <div className="flex flex-col px-6 py-4">
                            {NAV_LINKS.map((link) => (
                                <button
                                    key={link.href}
                                    data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                                    onClick={() => go(link.href)}
                                    className="flex items-baseline gap-3 py-4 border-b border-[#E5E5E5] text-left font-heading uppercase tracking-[0.15em] text-lg text-[#111111]"
                                >
                                    <span className="text-xs text-copper">{link.index}</span>
                                    {link.label}
                                </button>
                            ))}
                            <button
                                data-testid="nav-mobile-quote-button"
                                onClick={() => go("#contact")}
                                className="mt-4 mb-2 bg-copper text-[#FAFAFA] px-5 py-3 text-sm font-semibold uppercase tracking-[0.15em]"
                            >
                                Get a Quote
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
